import { Children, Fragment } from 'react';
import { Tab as HeadlessTab } from '@headlessui/react';
import { SectionCard } from 'components/SectionCard';
import { UnstyledButton } from 'utils/fragments';
import styled from 'styled-components';

export const Tab = styled(UnstyledButton).attrs(
    ({ mixpanelContext = 'Tab' }) => ({
        mixpanelContext,
    }),
)`
    color: ${({ theme, $selected }) =>
        $selected ? theme.purple_1 : theme.gray_2};
    text-transform: capitalize;
    border-bottom: solid 1px
        ${({ theme, $selected }) =>
            $selected ? theme.purple_1 : 'transparent'};
    font-weight: 500;
    padding: ${({ theme }) => theme.space_5}px 0px;
    display: flex;
    align-items: center;

    &:hover,
    &:focus-visible {
        color: ${({ theme }) => theme.purple_1};
    }
`;

export const TabListWrapper = styled.div`
    display: flex;
    justify-content: center;
    border-bottom: solid 1px ${({ theme }) => theme.gray_5};

    & > * {
        margin-left: ${({ theme }) => theme.space_13}px;
    }

    & > :first-child {
        margin-left: 0px;
    }
`;

const DefaultTabComponent = ({ children }) => children;

function List({ tabNames, ...props }) {
    return (
        <HeadlessTab.List as={TabListWrapper} {...props}>
            {tabNames.map((maybeTabName) => {
                let tabName = maybeTabName;
                let TabComponent = DefaultTabComponent;

                if (typeof maybeTabName !== 'string') {
                    tabName = maybeTabName.tabName;
                    TabComponent = maybeTabName.Component;
                }

                return (
                    <HeadlessTab as={Fragment} key={tabName}>
                        {({ selected }) => (
                            <Tab
                                $selected={selected}
                                mixpanelMeta={{ contentKey: tabName }}
                            >
                                <TabComponent $selected={selected}>
                                    <span>{tabName}</span>
                                </TabComponent>
                            </Tab>
                        )}
                    </HeadlessTab>
                );
            })}
        </HeadlessTab.List>
    );
}

function Panels({ children, ...props }) {
    return (
        <HeadlessTab.Panels {...props}>
            {Children.map(children, (child) => (
                <HeadlessTab.Panel key={child.key}>{child}</HeadlessTab.Panel>
            ))}
        </HeadlessTab.Panels>
    );
}

function Group({ children, selectedIndex, onChange, ...props }) {
    return (
        <SectionCard
            $space={0}
            css="padding: 0px; min-height: calc(100vh - 128px);"
            {...props}
        >
            <HeadlessTab.Group {...{ selectedIndex, onChange }}>
                {children}
            </HeadlessTab.Group>
        </SectionCard>
    );
}

Tab.Group = Group;
Tab.List = List;
Tab.Panels = Panels;

export default Tab;
